import { useEffect, useRef, useState } from 'react';
import { listMailingManualGroups, setMailingGroupMember, type MailingManualGroup } from '../api/affilmaster';
import type { MailingSpace } from '../api/mailing';
import './mailing-group-picker.css';

export function MailingGroupPicker({ email, onChange, space = 'affilmaster' }: { email: string; onChange: () => void; space?: MailingSpace }) {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState<MailingManualGroup[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    listMailingManualGroups(email, space)
      .then((g) => !cancelled && setGroups(g))
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : 'Načtení skupin selhalo'));
    const close = (ev: MouseEvent) => { if (ref.current && !ref.current.contains(ev.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => { cancelled = true; document.removeEventListener('mousedown', close); };
  }, [open, email, space]);

  async function toggle(g: MailingManualGroup) {
    setBusy(g.id); setError(null);
    try {
      await setMailingGroupMember(g.id, email, !g.member, space);
      setGroups((cur) => cur?.map((x) => (x.id === g.id ? { ...x, member: !g.member } : x)) ?? cur);
      onChange();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Uložení selhalo');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="am-group-picker" ref={ref}>
      <button type="button" className="am-group-picker-toggle" onClick={() => setOpen((o) => !o)} title="Přidat do skupiny" aria-expanded={open}>
        + skupina
      </button>
      {open && (
        <div className="am-group-picker-menu" role="menu">
          <strong>Ruční přidání do skupiny</strong>
          {!groups && !error && <span className="am-group-picker-note">Načítám…</span>}
          {groups && groups.length === 0 && <span className="am-group-picker-note">Žádná aktivní skupina nemá pravidlo Ruční přidání.</span>}
          {groups?.map((g) => (
            <label key={g.id} className="am-group-picker-item">
              <input type="checkbox" checked={g.member} disabled={busy !== null} onChange={() => toggle(g)} />
              <i style={{ background: g.color }} />
              <span>{g.name}</span>
            </label>
          ))}
          {error && <span className="am-group-picker-note is-error">{error}</span>}
        </div>
      )}
    </div>
  );
}
